import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Tier, EducationalInvestment, PageHeader } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { CareerPathWithScore, CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { isUnsupportedEnrollmentType } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/assessMyTrack.utils';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { selectCareerQuestions } from '@backrs_ui/grow-ui/src/redux/survey/survey';
import { determineOwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';

import { Box, SxProps, Theme } from '@mui/material';

import { AreYouOnTrackCTA } from './AreYouOnTrackCTA';
import { useAgentContext } from './BlueprintSection.agentContext';
import { BlueprintSectionCard } from './BlueprintSectionCard';
import { CareerPathRankings } from './CareerPathRankings';
import { CareerPathsCard } from './CareerPathsCard';
import { ConnectingLines, LineSegment } from './ConnectingLines';
import { EducationalInvestmentDetail } from './EducationalInvestmentDetail';
import { EducationalInvestmentsCard } from './EducationalInvestmentsCard';
import { ExploreTradeoffsTrigger } from './ExploreTradeoffs/ExploreTradeoffsTrigger';
import { MinimumTrackCard } from './MinimumTrackCard';
import { MinimumTrackDetail } from './MinimumTrackDetail';
import { OwnershipCard } from './OwnershipCard';
import { OwnershipDetail } from './OwnershipDetail';

const STRINGS = appStrings.modules.survey.output.blueprintSection;

type SectionKey = 'minimumTrack' | 'educationalInvestments' | 'careerPaths' | 'ownership';

/** Maps each section key to its card element ID for connecting line positioning */
const SECTION_CARD_ID: Record<SectionKey, string> = {
  minimumTrack: 'blueprint-card-minimum-track-card',
  educationalInvestments: 'blueprint-card-educational-investments-card',
  careerPaths: 'blueprint-card-career-paths-card',
  ownership: 'blueprint-card-ownership-card',
};

type BlueprintSectionProps = Readonly<{
  tier: Tier;
  careerPathSuggestions: CareerPathWithScore[];
  educationalInvestment?: { target: EducationalInvestment[]; others: EducationalInvestment[] };
  userResponseSet: CareerUserResponseSet;
  isShared?: boolean;
  isReadOnly?: boolean;
}>;

export function BlueprintSection({
  tier,
  careerPathSuggestions,
  educationalInvestment,
  userResponseSet,
  isShared = false,
  isReadOnly = false,
}: BlueprintSectionProps) {
  const [expandedSection, setExpandedSection] = useState<SectionKey | null>(null);
  // visibleSection trails expandedSection — it holds the old value during fade-out
  // so the content doesn't swap until the fade-out finishes
  const [visibleSection, setVisibleSection] = useState<SectionKey | null>(null);
  const [detailOpacity, setDetailOpacity] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardGridRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  const { data: profileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: isShared,
  });
  const academicEnrollmentTypes = profileData?.profile.academicEnrollmentTypes;
  const canAccessAssessMyTrack = !(
    !!academicEnrollmentTypes && isUnsupportedEnrollmentType(academicEnrollmentTypes)
  );

  const handleToggle = (section: SectionKey) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  // Orchestrate fade transitions when expandedSection changes
  useEffect(() => {
    if (expandedSection === visibleSection) return;

    const scrollToDetail = () => {
      // Double RAF ensures React has committed the re-render and the ref is attached
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
    };

    if (visibleSection !== null) {
      // Fade out first, then swap content and fade in
      setDetailOpacity(0);
      const timer = setTimeout(() => {
        setVisibleSection(expandedSection);
        if (expandedSection !== null) {
          requestAnimationFrame(() => {
            setDetailOpacity(1);
            scrollToDetail();
          });
        }
      }, FADE_MS);

      return () => clearTimeout(timer);
    }

    // No previous section — just show immediately with fade in
    setVisibleSection(expandedSection);
    const rafId = requestAnimationFrame(() => {
      setDetailOpacity(expandedSection !== null ? 1 : 0);
      if (expandedSection !== null) scrollToDetail();
    });

    return () => cancelAnimationFrame(rafId);
  }, [expandedSection, visibleSection]);

  const careerQuestions = useAppSelector(selectCareerQuestions);

  const strongFitCount = careerPathSuggestions.filter((p) => p.scoreSection === 'TOP').length;

  const computedOwnership =
    userResponseSet.ownership_level ??
    determineOwnershipLevel(tier, userResponseSet, careerQuestions);

  if (!computedOwnership) {
    console.warn('Ownership level could not be determined — falling back to "important"');
  }

  const ownershipLevel = computedOwnership ?? 'important';

  useAgentContext({
    tier,
    careerPathSuggestions,
    educationalInvestment,
    ownershipLevel,
  });

  const detailRenderers: Record<SectionKey, () => ReactNode> = {
    minimumTrack: () => <MinimumTrackDetail tier={tier} userResponseSet={userResponseSet} />,
    educationalInvestments: () => (
      <EducationalInvestmentDetail educationalInvestment={educationalInvestment} />
    ),
    careerPaths: () => (
      <CareerPathRankings careerPathSuggestions={careerPathSuggestions} isShared={isShared} />
    ),
    ownership: () => <OwnershipDetail />,
  };

  const calculateSectionLine = useCallback(
    (container: HTMLDivElement): LineSegment[] => {
      const grid = cardGridRef.current;
      const detail = detailRef.current;

      if (!grid || !detail || !visibleSection) return [];

      const cardEl = container.querySelector(
        `#${SECTION_CARD_ID[visibleSection]}`,
      ) as HTMLElement | null;

      if (!cardEl) return [];

      const containerRect = container.getBoundingClientRect();
      const cardRect = cardEl.getBoundingClientRect();
      const detailRect = detail.getBoundingClientRect();

      const centerX = cardRect.left + cardRect.width / 2 - containerRect.left;

      // Count visible card slots (exclude detail element and display:contents elements)
      const gridChildren = Array.from(grid.children) as HTMLElement[];
      const visibleSlots = gridChildren.filter(
        (el) => el !== detail && el.getBoundingClientRect().height > 0,
      );
      const firstSlot = visibleSlots[0];
      const columnsInFirstRow = firstSlot
        ? visibleSlots.filter(
            (child) =>
              Math.abs(child.getBoundingClientRect().top - firstSlot.getBoundingClientRect().top) <
              10,
          ).length
        : 1;

      // In multi-column mode (2+), use the row bottom so the line starts below all cards.
      // In 2-column mode for EI, use the card bottom for a longer line.
      // In 1-column mode, use the card bottom since the detail is directly underneath.
      const isMultiColumn = columnsInFirstRow > 1;
      const useExtendedLine =
        columnsInFirstRow === 2 && visibleSection === 'educationalInvestments';

      let y1: number;

      if (isMultiColumn && !useExtendedLine) {
        const cardsBottom = Math.max(
          ...visibleSlots.map((el) => el.getBoundingClientRect().bottom),
        );

        y1 = cardsBottom - containerRect.top;
      } else {
        y1 = cardRect.bottom - containerRect.top;
      }

      const y2 = detailRect.top - containerRect.top;

      return [{ x1: centerX, y1, x2: centerX, y2 }];
    },
    [visibleSection],
  );

  return (
    <Box ref={containerRef} sx={containerSx}>
      <PageHeader
        title={STRINGS.title}
        subtitle={STRINGS.subtitle}
        maxWidth={null}
        rightComponents={[
          <LubavButton
            key="blueprint-explain"
            id="blueprint-explain-btn"
            label="Explain this to me"
            chatMessage="Explain my blueprint to me"
            promptButtonId="blueprint_explain"
          />,
        ]}
      />

      {/* 3-column card grid: Career Track | Education | Career Paths (stacked with Ownership) */}
      <Box ref={cardGridRef} sx={cardGridSx}>
        <BlueprintSectionCard
          id="blueprint-card-minimum-track"
          title={STRINGS.minimumTrack.title}
          isExpanded={expandedSection === 'minimumTrack'}
          onToggle={() => handleToggle('minimumTrack')}
          lubavPromptButtonId="blueprint_min_career_track"
          lubavChatMessage="Discuss my minimum career track"
        >
          <MinimumTrackCard tier={tier} />
        </BlueprintSectionCard>

        <BlueprintSectionCard
          id="blueprint-card-educational-investments"
          title={STRINGS.educationalInvestments.title}
          isExpanded={expandedSection === 'educationalInvestments'}
          onToggle={() => handleToggle('educationalInvestments')}
          lubavPromptButtonId="blueprint_educational_investments"
          lubavChatMessage="Discuss my educational investments"
        >
          <EducationalInvestmentsCard educationalInvestment={educationalInvestment} />
        </BlueprintSectionCard>

        {/* Third column: Career Paths + Ownership stacked */}
        <Box sx={stackedColumnSx}>
          <BlueprintSectionCard
            id="blueprint-card-career-paths"
            title={STRINGS.careerPaths.title(strongFitCount)}
            isExpanded={expandedSection === 'careerPaths'}
            onToggle={() => handleToggle('careerPaths')}
          >
            <CareerPathsCard careerPathSuggestions={careerPathSuggestions} />
          </BlueprintSectionCard>

          <BlueprintSectionCard
            id="blueprint-card-ownership"
            title={STRINGS.ownership.title}
            isExpanded={expandedSection === 'ownership'}
            onToggle={() => handleToggle('ownership')}
            lubavPromptButtonId="blueprint_ownership"
            lubavChatMessage="Discuss my ownership level"
          >
            <OwnershipCard ownershipLevel={ownershipLevel} />
          </BlueprintSectionCard>
        </Box>

        {/* Detail area — in 1-column mode, CSS order places this after the selected card */}
        {visibleSection && (
          <Box ref={detailRef} sx={detailAreaSx(detailOpacity, visibleSection)}>
            {detailRenderers[visibleSection]()}
          </Box>
        )}
      </Box>

      {/* Connecting line from selected card to detail area */}
      {visibleSection && (
        <ConnectingLines containerRef={containerRef} calculate={calculateSectionLine} />
      )}

      {!isReadOnly && (
        <>
          <ExploreTradeoffsTrigger />
          {canAccessAssessMyTrack && <AreYouOnTrackCTA />}
        </>
      )}
    </Box>
  );
}

// --- Styles ---

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const cardGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gap: backrs.spacing['spacing-2xl'],
  gridTemplateColumns: '1fr',
  // 1-column: assign order to card slots so the detail can be interleaved.
  // The stacked column uses display:contents in 1-col, so its children are direct grid items.
  '& > :nth-child(1)': { order: 0 }, // Minimum Track
  '& > :nth-child(2)': { order: 2 }, // Educational Investments
  // Stacked column's children (via display:contents):
  '& > :nth-child(3) > :nth-child(1)': { order: 4 }, // Career Paths
  '& > :nth-child(3) > :nth-child(2)': { order: 6 }, // Ownership
  '@container (min-width: 500px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    // Reset all child orders for natural grid flow in multi-column
    '& > *': { order: '0 !important' },
    '& > :nth-child(3) > *': { order: '0 !important' },
  },
  '@container (min-width: 750px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const stackedColumnSx: SxProps<Theme> = ({ backrs }) => ({
  // 1-column: flatten children into the grid so detail can interleave
  display: 'contents',
  '@container (min-width: 500px)': {
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-2xl'],
    '& > :first-of-type': {
      flex: 5,
    },
    '& > :last-of-type': {
      flex: 2,
    },
  },
});

const FADE_MS = 200;
const FADE_TRANSITION = `opacity ${FADE_MS}ms ease-in-out`;

/** Maps section to CSS order for 1-column interleaving (between card orders 0, 2, 4, 6) */
const DETAIL_ORDER: Record<SectionKey, number> = {
  minimumTrack: 1,
  educationalInvestments: 3,
  careerPaths: 5,
  ownership: 7,
};

const detailAreaSx =
  (opacity: number, section: SectionKey): SxProps<Theme> =>
  ({ backrs }) => ({
    order: DETAIL_ORDER[section],
    gridColumn: '1 / -1',
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors['Brand/500']}`,
    backgroundColor: backrs.colors['Colors/Base/White'],
    padding: backrs.spacing['spacing-3xl'],
    opacity,
    transition: FADE_TRANSITION,
  });
